(() => {
  const config = document.querySelector("[data-japotyle-favorites-config]");
  if (!config || window.JapotyleFavorites) return;

  const storageKey = "japotyle:favorites:v1";
  const endpoint = config.dataset.endpoint || "/apps/japotyle-search/v1/favorites";
  const authenticated = config.dataset.authenticated === "true";
  const labels = {
    add: config.dataset.labelAdd || "Add to favorites",
    remove: config.dataset.labelRemove || "Remove from favorites",
    empty: config.dataset.labelEmpty || "No favorites yet.",
    syncError: config.dataset.labelSyncError || "Favorites could not be synced.",
    loading: config.dataset.labelLoading || "Loading…",
    view: config.dataset.labelView || "View product",
    savedCount: config.dataset.labelSavedCount || "%count% saved items"
  };
  const state = new Map();
  let ready = false;

  const setTextIfChanged = (node, value) => {
    if (node && node.textContent !== value) node.textContent = value;
  };

  const setAttributeIfChanged = (node, name, value) => {
    if (node.getAttribute(name) !== value) node.setAttribute(name, value);
  };

  const normalizeId = (value) => {
    const match = String(value || "").match(/(?:Product\/)?(\d+)$/);
    return match ? match[1] : "";
  };

  const localizedUrl = (value) => {
    if (!value) return "#";
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return value;
      const root = String(window.Shopify?.routes?.root || "/").replace(/\/$/, "");
      const path = url.pathname.replace(/^\/(?:ko|id|th|vi|zh-hans|zh-hant)(?=\/|$)/, "");
      return `${root}${path || "/"}${url.search}${url.hash}`.replace(/^\/\//, "/");
    } catch (_error) {
      return value;
    }
  };

  const readLocal = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  };

  const writeLocal = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(state.values()).slice(0, 500)));
    } catch (_error) {
      // Favorites still work for this page when browser storage is unavailable.
    }
  };

  const buttonItem = (button) => ({
    product_id: normalizeId(button.dataset.productId),
    handle: button.dataset.productHandle || "",
    title: button.dataset.productTitle || "",
    product_url: button.dataset.productUrl || "",
    image_url: button.dataset.productImage || "",
    display_price: button.dataset.productPrice || "",
    created_at: new Date().toISOString()
  });

  const setButtonState = (button) => {
    const active = state.has(normalizeId(button.dataset.productId));
    const label = active ? labels.remove : labels.add;
    if (button.classList.contains("is-active") !== active) {
      button.classList.toggle("is-active", active);
    }
    setAttributeIfChanged(button, "aria-pressed", String(active));
    setAttributeIfChanged(button, "aria-label", label);
    if (button.title !== label) button.title = label;
    const text = button.querySelector("[data-japotyle-favorite-label]");
    setTextIfChanged(text, label);
  };

  const refreshUi = () => {
    document.querySelectorAll("[data-japotyle-favorite]").forEach(setButtonState);
    document.querySelectorAll("[data-japotyle-favorite-count]").forEach((node) => {
      setTextIfChanged(node, String(state.size));
      const hidden = state.size === 0;
      if (node.hidden !== hidden) node.hidden = hidden;
    });
    document.querySelectorAll("[data-japotyle-favorites-link]").forEach((link) => {
      setAttributeIfChanged(link, "aria-label", labels.savedCount.replace("%count%", String(state.size)));
    });
    renderPage();
  };

  const request = async (method, body) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set("locale", config.dataset.locale || "en");
    url.searchParams.set("country", config.dataset.country || "");
    const response = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.ok === false) {
      throw new Error(payload.error || labels.syncError);
    }
    return payload;
  };

  const mergeRemote = (items) => {
    state.clear();
    (items || []).forEach((item) => {
      const id = normalizeId(item.product_id || item.shopify_product_id);
      if (id) state.set(id, { ...item, product_id: id });
    });
    writeLocal();
  };

  const showStatus = (message, isError = false) => {
    document.querySelectorAll("[data-japotyle-favorites-status]").forEach((node) => {
      setTextIfChanged(node, message);
      const hidden = !message;
      if (node.hidden !== hidden) node.hidden = hidden;
      if (node.classList.contains("is-error") !== isError) {
        node.classList.toggle("is-error", isError);
      }
    });
  };

  const createCard = (item) => {
    const card = document.createElement("article");
    card.className = "japotyle-favorites-card";
    const link = document.createElement("a");
    link.className = "japotyle-favorites-card__media";
    link.href = localizedUrl(item.product_url || `/products/${item.handle}`);
    if (item.image_url) {
      const image = document.createElement("img");
      image.src = item.image_url;
      image.alt = item.title || "";
      image.loading = "lazy";
      link.append(image);
    }
    const body = document.createElement("div");
    body.className = "japotyle-favorites-card__body";
    const title = document.createElement("a");
    title.href = link.href;
    title.textContent = item.title || item.handle || "Saved product";
    title.className = "japotyle-favorites-card__title";
    const price = document.createElement("strong");
    const amounts = (item.condition_data || [])
      .map((variant) => Number(variant.price || 0))
      .filter((amount) => amount > 0);
    if (amounts.length) {
      price.textContent = new Intl.NumberFormat(config.dataset.locale || "en", {
        style: "currency",
        currency: item.condition_data?.find((variant) => variant.currency)?.currency || config.dataset.currency || "USD"
      }).format(Math.min(...amounts));
    } else {
      price.textContent = item.display_price || "";
    }
    const actions = document.createElement("div");
    actions.className = "japotyle-favorites-card__actions";
    const view = document.createElement("a");
    view.href = link.href;
    view.textContent = labels.view;
    const remove = document.createElement("button");
    remove.type = "button";
    remove.textContent = labels.remove;
    remove.dataset.japotyleFavorite = "";
    remove.dataset.productId = item.product_id;
    remove.dataset.productHandle = item.handle || "";
    remove.dataset.productTitle = item.title || "";
    remove.dataset.productUrl = item.product_url || "";
    remove.dataset.productImage = item.image_url || "";
    actions.append(view, remove);
    body.append(title, price, actions);
    card.append(link, body);
    return card;
  };

  function renderPage() {
    const grid = document.querySelector("[data-japotyle-favorites-grid]");
    const empty = document.querySelector("[data-japotyle-favorites-empty]");
    if (!grid || !ready) return;
    grid.replaceChildren(...Array.from(state.values()).map(createCard));
    if (empty) empty.hidden = state.size > 0;
  }

  const toggle = async (button) => {
    const item = buttonItem(button);
    if (!item.product_id || button.disabled) return;
    const wasActive = state.has(item.product_id);
    const previous = state.get(item.product_id);
    button.disabled = true;
    if (wasActive) state.delete(item.product_id);
    else state.set(item.product_id, item);
    writeLocal();
    refreshUi();
    try {
      if (authenticated) {
        const result = wasActive
          ? await request("DELETE", { product_id: item.product_id })
          : await request("POST", { product_id: item.product_id });
        if (!wasActive && (result.rejected_product_ids || []).length) {
          throw new Error(labels.syncError);
        }
      }
      showStatus("");
    } catch (error) {
      if (wasActive && previous) state.set(item.product_id, previous);
      else state.delete(item.product_id);
      writeLocal();
      refreshUi();
      showStatus(error.message || labels.syncError, true);
    } finally {
      button.disabled = false;
      setButtonState(button);
    }
  };

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-japotyle-favorite]");
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggle(button);
  });

  const syncAddedFavoriteButtons = (node) => {
    if (!node || node.nodeType !== 1) return;
    if (node.matches("[data-japotyle-favorite]")) setButtonState(node);
    node.querySelectorAll("[data-japotyle-favorite]").forEach(setButtonState);
  };

  const observer = new MutationObserver((mutations) => {
    if (!ready) return;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(syncAddedFavoriteButtons);
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.JapotyleFavorites = {
    refresh: refreshUi,
    has: (productId) => state.has(normalizeId(productId))
  };

  (async () => {
    readLocal().forEach((item) => {
      const id = normalizeId(item.product_id || item.shopify_product_id);
      if (id) state.set(id, { ...item, product_id: id });
    });
    showStatus(labels.loading);
    if (authenticated) {
      try {
        const localIds = Array.from(state.keys());
        if (localIds.length) await request("POST", { product_ids: localIds });
        const remote = await request("GET");
        mergeRemote(remote.items || []);
        showStatus("");
      } catch (error) {
        showStatus(error.message || labels.syncError, true);
      }
    } else {
      showStatus("");
    }
    ready = true;
    refreshUi();
  })();
})();
